import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { getModalLayerOpen, subscribeModalLayer } from '../lib/modalLayer'
import { DIZEE_USER_STORAGE_EVENT, getActiveArtistIds } from '../lib/api'
import { useTheme } from '../context/ThemeContext'
import ArtistSwitcher from './ArtistSwitcher'
import { useUserPermissions } from '../hooks/useUserPermissions'
import { getConversationHistory, getLocalConversations } from '../lib/chatApi'
import { AGENT_SPECIALISTS } from '../lib/agentSpecialists'
import { SidebarChatHistorySkeleton } from './ui/Skeleton'
import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  BookOpen,
  Bot,
  Calendar,
  Handshake,
  LayoutDashboard,
  Link2,
  ListTodo,
  MapPinned,
  Mic2,
  Music,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Users,
  Wallet,
} from 'lucide-react'
type NavItemDef = { href: string; label: string; color: string; icon: LucideIcon }

const navItems: NavItemDef[] = [
  { href: '/dashboard', label: 'Dashboard', color: '#818cf8', icon: LayoutDashboard },
  { href: '/agents/setup', label: 'Agents', color: '#6366f1', icon: Bot },
  { href: '/activity', label: 'Activity', color: '#6366f1', icon: Activity },
  { href: '/schedule', label: 'Schedule', color: '#f59e0b', icon: Calendar },
  { href: '/music', label: 'Music', color: '#1DB954', icon: Music },
  { href: '/shows', label: 'Shows', color: '#9333ea', icon: Mic2 },
  { href: '/tour-manager', label: 'Touring', color: '#f97316', icon: MapPinned },
  { href: '/tasks', label: 'Tasks', color: '#FF6B35', icon: ListTodo },
  { href: '/money', label: 'Money', color: '#10b981', icon: Wallet },
  { href: '/deals', label: 'Deals', color: '#ea580c', icon: Handshake },
  { href: '/links', label: 'Links', color: '#0ea5e9', icon: Link2 },
  { href: '/team', label: 'Team', color: '#8b5cf6', icon: Users },
  { href: '/settings', label: 'Settings', color: '#64748b', icon: Settings },
  { href: '/knowledge', label: 'Guides', color: '#f59e0b', icon: BookOpen },
]

/** Toggle to show the Agents block (setup, chat, specialists, history) in the sidebar. */
const SHOW_AGENTS_SIDEBAR = false

// ── Chat history helpers ─────────────────────────────────────────────
interface ConvSummary {
  _id: string
  conversationId?: string
  title?: string
  lastMessage?: string
  createdAt?: string
  updatedAt?: string
  messages?: Array<{ type?: string; role?: string; content: string }>
}

function getConvTitle(c: ConvSummary): string {
  if (c.title) return c.title
  if (c.lastMessage) return c.lastMessage.slice(0, 50)
  const first = c.messages?.find((m) => m.role === 'user' || m.type === 'user')
  if (first) return first.content.slice(0, 50)
  return 'New conversation'
}

function getDateGroup(dateStr?: string): string {
  if (!dateStr) return 'Older'
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86_400_000)
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000)
  if (d >= today) return 'Today'
  if (d >= yesterday) return 'Yesterday'
  if (d >= weekAgo) return 'This week'
  return 'Older'
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'Older']

// ── Component ────────────────────────────────────────────────────────
interface SidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
}

export default function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const modalLayerOpen = useSyncExternalStore(subscribeModalLayer, getModalLayerOpen, () => false)
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const { hasPermission, loading: permissionsLoading } = useUserPermissions()
  
  const [user, setUser] = useState<any | null>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    const syncUserFromStorage = () => {
      try {
        const raw = localStorage.getItem('dizeeUser')
        setUser(raw ? JSON.parse(raw) : null)
      } catch {
        setUser(null)
      }
    }
    window.addEventListener(DIZEE_USER_STORAGE_EVENT, syncUserFromStorage)
    return () => window.removeEventListener(DIZEE_USER_STORAGE_EVENT, syncUserFromStorage)
  }, [])

  // ── Chat history state ──
  // Initialize from localStorage immediately so conversations show instantly
  const [chatConvs, setChatConvs] = useState<ConvSummary[]>(() => getLocalConversations() as any[])
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false)
  const [agentsExpanded, setAgentsExpanded] = useState(false)

  const onChatPage = location.pathname === '/chat'
  const onAgentsPage = location.pathname.startsWith('/agents')
  const activeConvId = onChatPage
    ? new URLSearchParams(location.search).get('id') || ''
    : ''

  // Expand Agents when on chat or any agent route; load history when expanded or on /chat
  useEffect(() => {
    if (onChatPage || onAgentsPage) setAgentsExpanded(true)
  }, [onChatPage, onAgentsPage])

  useEffect(() => {
    if ((agentsExpanded || onChatPage) && !chatHistoryLoaded) {
      fetchChatHistory()
    }
  }, [agentsExpanded, onChatPage, chatHistoryLoaded])

  // Listen for new-conversation events from ChatPage (same tab)
  useEffect(() => {
    const handler = () => {
      // Re-read from localStorage (instant) + async API merge
      setChatConvs(getLocalConversations() as any[])
      fetchChatHistory()
    }
    window.addEventListener('dizee:new-conversation', handler)
    return () => window.removeEventListener('dizee:new-conversation', handler)
  }, [])

  // Listen for localStorage changes from OTHER tabs (cross-tab sync)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'dizee:chatConversations') {
        try {
          const updated = JSON.parse(e.newValue || '[]')
          setChatConvs(updated)
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const fetchChatHistory = async () => {
    try {
      const r = await getConversationHistory()
      if (r.success && r.conversations) {
        setChatConvs(r.conversations)
      }
    } catch {
      /* silent */
    }
    setChatHistoryLoaded(true)
  }

  // Group conversations by date
  const grouped = chatConvs.reduce<Record<string, ConvSummary[]>>((acc, c) => {
    const g = getDateGroup(c.updatedAt || c.createdAt)
    ;(acc[g] ??= []).push(c)
    return acc
  }, {})

  // Re-read active artists when they change (e.g. toggled on Team Hub)
  const [activeArtistIds, setActiveArtistIds] = useState<string[]>(() => getActiveArtistIds())
  useEffect(() => {
    const sync = () => setActiveArtistIds(getActiveArtistIds())
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  const isTeamMember = user?.professionRole && ['manager', 'support', 'promoter'].includes(user.professionRole)
  const isActingAsArtist = isTeamMember && activeArtistIds.length > 0

  /**
   * Team + acting as ≥1 artist: hide real nav until member-permissions has settled once (`navPermHydrated`).
   * On cold login, `getActiveArtistIds()` is often [] on the first tick — permissions hook thinks "no artists"
   * and finishes loading before IDs restore; on refresh IDs exist immediately. Reset hydration when we go 0→>0.
   * While hydrated, refetches keep `permissionsLoading` true but we still show stale links (no skeleton flash).
   */
  const [navPermHydrated, setNavPermHydrated] = useState(false)
  const prevActiveArtistCountRef = useRef<number | null>(null)

  useEffect(() => {
    if (!user) setNavPermHydrated(false)
  }, [user])

  useLayoutEffect(() => {
    if (!isTeamMember) return
    const n = activeArtistIds.length
    if (prevActiveArtistCountRef.current === null) {
      prevActiveArtistCountRef.current = n
      return
    }
    if (prevActiveArtistCountRef.current === 0 && n > 0) {
      setNavPermHydrated(false)
    }
    prevActiveArtistCountRef.current = n
  }, [isTeamMember, activeArtistIds.length])

  useEffect(() => {
    if (!permissionsLoading && isTeamMember) {
      setNavPermHydrated(true)
    }
  }, [permissionsLoading, isTeamMember])

  const showSidebarNavSkeleton =
    Boolean(isTeamMember) && activeArtistIds.length > 0 && !navPermHydrated

  // Map of route paths to permission section IDs
  const routeToSectionMap: Record<string, string> = {
    '/schedule': 'schedule',
    '/shows': 'shows',
    '/tour-manager': 'shows',
    '/tasks': 'tasks',
    '/music': 'music',
    '/links': 'links',
    '/knowledge': 'knowledge',
    '/team': 'team',
    '/settings': 'settings',
    '/activity': 'activity',
    '/money': 'finance',
    '/deals': 'deals',
    '/dashboard': 'activity',
    '/chat': 'chat',
    '/agents/setup': 'chat',
  }

  // Filter nav items based on context and permissions
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      // Hide Guides and Settings from the nav list (Settings is in the profile menu)
      if (item.href === '/knowledge' || item.href === '/settings') return false

      // Schedule is always visible for everyone
      if (item.href === '/schedule') return true

      // For non-team-members (artists), all items shown
      if (!isTeamMember) return true

      // ── Team member logic below ──
      // (Do not hide nav while permissions refetch after switching artists — stale permissions
      //  stay visible until the new snapshot loads so users can still switch artists and navigate.)

      // Agents, Team are always visible for team members
      if (item.href === '/agents/setup' || item.href === '/team') return true

      // If team member is NOT acting as an artist, only show granted sections
      if (!isActingAsArtist) return false

      // Team member IS acting as artist — check permissions
      const sectionId = routeToSectionMap[item.href]
      if (sectionId) {
        return hasPermission(sectionId)
      }
      return true
    })
  }, [isTeamMember, isActingAsArtist, hasPermission])

  /** App pages (Dashboard, Schedule, …) — not Agents */
  const visibleSectionItems = useMemo(
    () => visibleNavItems.filter((item) => item.href !== '/agents/setup'),
    [visibleNavItems]
  )

  const isNavActive = (href: string) => {
    if (href === '/agents/setup') {
      return location.pathname.startsWith('/agents') || location.pathname === '/chat'
    }
    return (
      location.pathname === href ||
      (href === '/tour-manager' && location.pathname.startsWith('/tour-manager/'))
    )
  }

  const getInitials = (name: string) => {
    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
    if (!parts.length) return 'U'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  const displayName =
    user?.name ||
    user?.username ||
    user?.email ||
    'Account'
  const avatarUrl = user?.avatar || user?.photo || user?.profile_image || null

  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileMenuOpen])

  /** ChatGPT-style section headers + rows (shared by Agents + Sections) */
  const sectionHeaderClass =
    'text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 select-none'
  const agentsRow = (active: boolean) =>
    `sidebar-tooltip-wrap flex w-full min-h-[36px] min-w-0 items-center rounded-md px-2 py-1.5 text-sm touch-manipulation transition-colors ${
      active
        ? 'font-medium text-gray-900 dark:text-gray-100 bg-gray-100/90 dark:bg-white/[0.08]'
        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50/90 dark:hover:bg-white/[0.06]'
    }`

  const handleLogout = () => {
    // Persist active artist toggles per-user so they survive logout/login
    const currentIds = localStorage.getItem('dizee:activeArtistIds')
    if (currentIds) {
      if (user?.email) localStorage.setItem(`dizee:activeArtistIds:${user.email}`, currentIds)
      const uid = user?._id || user?.id
      if (uid) localStorage.setItem(`dizee:activeArtistIds:${uid}`, currentIds)
    }
    localStorage.removeItem('dizeeToken')
    localStorage.removeItem('dizeeUser')
    localStorage.removeItem('selectedArtist')
    localStorage.removeItem('dizee:activeArtistIds')
    localStorage.removeItem('dizee:artistCache')
    localStorage.removeItem('isNewSignup')
    setUser(null)
    setIsOpen(false)
    navigate('/login')
  }

  const collapsedIconLinkClass = (active: boolean) =>
    `sidebar-tooltip-wrap relative text-sm rounded-md transition-colors touch-manipulation flex items-center justify-center py-2 px-0 ${
      active
        ? 'text-black font-medium bg-gray-50 active-nav'
        : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white hover:bg-gray-50/50 dark:hover:bg-white/[0.06]'
    }`

  const renderAgentsSidebar = () => (
    <li key="agents" className="mb-1">
      <div className="flex items-center justify-between gap-1 px-2 py-1.5">
        <span className={sectionHeaderClass}>Agents</span>
        <button
          type="button"
          onClick={() => setAgentsExpanded(!agentsExpanded)}
          className="p-0.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-white/10 dark:hover:text-gray-300 shrink-0"
          aria-expanded={agentsExpanded}
          aria-label={agentsExpanded ? 'Collapse agents' : 'Expand agents'}
        >
          <svg
            className={`w-3.5 h-3.5 transition-transform duration-200 ${agentsExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      {agentsExpanded && (
        <div className="mb-2 space-y-0.5">
          <Link
            to="/agents/setup"
            onClick={() => setIsOpen(false)}
            className={agentsRow(location.pathname === '/agents/setup')}
          >
            <span className="truncate">Agent setup</span>
          </Link>
          <Link
            to="/chat"
            onClick={() => setIsOpen(false)}
            className={agentsRow(location.pathname === '/chat' && !activeConvId)}
          >
            <span className="truncate">Chat</span>
          </Link>
          {AGENT_SPECIALISTS.map((s) => {
            const subActive = location.pathname === `/agents/${s.id}`
            return (
              <Link
                key={s.id}
                to={`/agents/${s.id}`}
                onClick={() => setIsOpen(false)}
                className={agentsRow(subActive)}
              >
                <span className="truncate">{s.label}</span>
              </Link>
            )
          })}

          {(chatConvs.length > 0 || !chatHistoryLoaded) && (
            <>
              <div className="flex items-center px-2 pt-3 pb-1">
                <span className={sectionHeaderClass}>Chats</span>
              </div>
              {!chatHistoryLoaded && (
                <div className="px-0 py-1">
                  <SidebarChatHistorySkeleton />
                </div>
              )}
              {chatConvs.length > 0 && (
                <div className="max-h-[38vh] overflow-y-auto space-y-2">
                  {GROUP_ORDER.map((g) => {
                    const items = grouped[g]
                    if (!items?.length) return null
                    return (
                      <div key={g}>
                        <p className={`${sectionHeaderClass} px-2 py-1`}>{g}</p>
                        <div className="space-y-0.5">
                          {items.map((c) => {
                            const id = c._id || c.conversationId || ''
                            const active = id === activeConvId
                            return (
                              <Link
                                key={id}
                                to={`/chat?id=${id}`}
                                onClick={() => setIsOpen(false)}
                                className={agentsRow(active)}
                              >
                                <span className="truncate">{getConvTitle(c)}</span>
                              </Link>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  )

  const SIDEBAR_NAV_SKELETON_ROWS = 10

  const renderSidebarNavSkeletonExpanded = () => (
    <li key="sections-skeleton" className="mb-1">
      <div className="mb-2 space-y-0.5" aria-busy="true" aria-label="Loading navigation">
        {Array.from({ length: SIDEBAR_NAV_SKELETON_ROWS }).map((_, i) => (
          <div
            key={i}
            className="flex min-h-[36px] w-full items-center rounded-md px-2 py-1.5"
          >
            <div
              className="h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
              style={{ width: `${42 + (i % 5) * 8}%`, maxWidth: '11rem' }}
            />
          </div>
        ))}
      </div>
    </li>
  )

  const renderSidebarNavSkeletonCollapsed = () => (
    <>
      {Array.from({ length: SIDEBAR_NAV_SKELETON_ROWS }).map((_, i) => (
        <li key={`nav-sk-${i}`}>
          <div className="sidebar-tooltip-wrap flex justify-center py-2 px-0">
            <div className="h-[18px] w-[18px] rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse" aria-hidden />
          </div>
        </li>
      ))}
    </>
  )

  const renderSectionsSidebar = () => (
    <li key="sections" className="mb-1">
      <div className="mb-2 space-y-0.5">
        {visibleSectionItems.map((item) => {
          const Icon = item.icon
          const active = isNavActive(item.href)
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setIsOpen(false)}
              className={agentsRow(active)}
            >
              <Icon
                className="h-[16px] w-[16px] shrink-0 mr-2.5"
                strokeWidth={1.75}
                style={{ color: item.color }}
                aria-hidden
              />
              <span className="truncate">{item.label}</span>
            </Link>
          )
        })}
      </div>
    </li>
  )

  const renderCollapsedSidebarNav = () => {
    const agentsActive = location.pathname.startsWith('/agents') || location.pathname === '/chat'
    const agentsItem = navItems.find((i) => i.href === '/agents/setup')

    const sectionIconLinks = visibleSectionItems.map((item) => {
      const Icon = item.icon
      return (
        <li key={item.href}>
          <Link
            to={item.href}
            onClick={() => setIsOpen(false)}
            className={collapsedIconLinkClass(isNavActive(item.href))}
            title={item.label}
            aria-label={item.label}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} style={{ color: item.color }} aria-hidden />
            <span className="sidebar-tooltip">{item.label}</span>
          </Link>
        </li>
      )
    })

    if (!SHOW_AGENTS_SIDEBAR) {
      return <>{sectionIconLinks}</>
    }

    if (!agentsItem) {
      return <>{sectionIconLinks}</>
    }

    const AgentsIcon = agentsItem.icon
    return (
      <>
        <li key="agents-collapsed">
          <Link
            to="/agents/setup"
            onClick={() => setIsOpen(false)}
            className={collapsedIconLinkClass(agentsActive)}
            title={agentsItem.label}
            aria-label={agentsItem.label}
          >
            <AgentsIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="sidebar-tooltip">{agentsItem.label}</span>
          </Link>
        </li>
        {sectionIconLinks}
      </>
    )
  }

  return (
    <>
      {/* Mobile menu button – hidden when sidebar is open or any modal is open (portals sit above z-60; also hide to avoid overlap glitches in WebView). */}
      {!isOpen && !modalLayerOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed z-[60] md:hidden p-2.5 rounded-lg bg-white/80 dark:bg-[rgb(var(--surface-rgb))]/80 backdrop-blur-sm border border-gray-200 dark:border-[rgb(var(--border-rgb))] shadow-sm touch-manipulation min-h-0 h-11 w-11 flex items-center justify-center"
          style={{
            top: 'max(1rem, env(safe-area-inset-top, 0px))',
            left: 'max(1rem, env(safe-area-inset-left, 0px))',
          }}
          aria-label="Open menu"
        >
          <svg className="w-5 h-5 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed
          top-0 left-0
          h-dvh
          pt-[env(safe-area-inset-top,0px)]
          ${isCollapsed ? 'md:w-20 md:overflow-visible' : 'md:w-72'}
          w-72
          bg-white sidebar-shell
          z-40
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{ transition: 'width 200ms ease-in-out, transform 200ms ease-in-out' }}
      >
        <div className="h-full flex flex-col">
          {/* Top section with logo and nav */}
          <div className={`flex-1 min-h-0 ${isCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
            <nav className={`${isCollapsed ? 'p-3' : 'pl-5 pr-6 pt-6 pb-6'}`}>
              <div
                className={`mb-8 flex h-8 w-full items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-2 md:justify-between'}`}
              >
                {!isCollapsed ? (
                  <>
                    <img src="/favicon.png" alt="DIZEE" className="h-8 w-8 shrink-0" />
                    <button
                      type="button"
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          setIsOpen(false)
                        } else {
                          onToggleCollapse()
                        }
                      }}
                      className="
                        hidden md:flex h-7 w-7 shrink-0 items-center justify-center rounded-lg
                        border border-gray-200 bg-gray-100 text-gray-500
                        shadow-sm transition-colors
                        hover:bg-gray-50 hover:text-gray-700
                        dark:border-zinc-600/80 dark:bg-zinc-900/90 dark:text-zinc-400
                        dark:hover:bg-zinc-800 dark:hover:text-zinc-200
                      "
                      aria-label="Minimize sidebar"
                    >
                      <PanelLeftClose className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="
                      flex h-7 w-7 shrink-0 items-center justify-center rounded-lg
                      border border-gray-200 bg-gray-100 text-gray-500
                      shadow-sm transition-colors
                      hover:bg-gray-50 hover:text-gray-700
                      dark:border-zinc-600/80 dark:bg-zinc-900/90 dark:text-zinc-400
                      dark:hover:bg-zinc-800 dark:hover:text-zinc-200
                    "
                    aria-label="Expand sidebar"
                  >
                    <PanelLeftOpen className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
                  </button>
                )}
              </div>

              {/* Artist Switcher — full control when expanded; compact icon when sidebar is collapsed */}
              {isTeamMember && (
                <div className={isCollapsed ? 'mb-4 flex justify-center' : 'mb-6'}>
                  <ArtistSwitcher compact={isCollapsed} />
                </div>
              )}

              <div className="space-y-1">
                <ul className="space-y-1">
                  {isCollapsed ? (
                    showSidebarNavSkeleton ? renderSidebarNavSkeletonCollapsed() : renderCollapsedSidebarNav()
                  ) : (
                    <>
                      {SHOW_AGENTS_SIDEBAR && !showSidebarNavSkeleton && renderAgentsSidebar()}
                      {showSidebarNavSkeleton ? renderSidebarNavSkeletonExpanded() : renderSectionsSidebar()}
                    </>
                  )}
                </ul>
              </div>
            </nav>
          </div>
          
          {/* Bottom section: theme toggle + profile */}
          <div
            ref={profileMenuRef}
            className={`flex-shrink-0 border-t border-gray-100 ${isCollapsed ? 'p-3 pt-3' : 'pl-5 pr-6 pt-3 pb-4'}`}
            style={{ paddingBottom: `max(${isCollapsed ? '0.75rem' : '1rem'}, calc(${isCollapsed ? '0.75rem' : '1rem'} + var(--sab, 0px)))` }}
          >
            {/* Theme toggle — always visible above profile */}
            <button
              onClick={toggleTheme}
              className={`
                w-full p-2 mb-2 rounded-md transition-colors
                ${isCollapsed ? 'flex justify-center' : 'flex items-center gap-2'}
                hover:bg-gray-50 dark:hover:bg-white/5
                text-gray-600 dark:text-gray-300
              `}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
              {!isCollapsed && (
                <span className="text-sm">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
              )}
            </button>

            {/* Clickable profile row */}
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className={`
                w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}
                py-2 rounded-md transition-colors
                hover:bg-gray-50 dark:hover:bg-white/5
              `}
              title={isCollapsed ? displayName : undefined}
              aria-label="Account menu"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="h-9 w-9 rounded-full object-cover border border-gray-200 shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center border border-gray-200 shrink-0">
                  {getInitials(String(displayName))}
                </div>
              )}
              {!isCollapsed && (
                <>
                  <div className="min-w-0 text-left flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</div>
                    <div className="text-xs text-gray-500 truncate">{user?.email || 'Signed in'}</div>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 ${profileMenuOpen ? 'rotate-180' : 'rotate-0'}`}
                    style={{ transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              )}
            </button>

            {/* Inline expanded menu — smooth grid-rows transition */}
            <div
              className="grid"
              style={{
                gridTemplateRows: profileMenuOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              <div className="overflow-hidden">
                <div className={`mt-1 space-y-0.5 ${isCollapsed ? '' : 'pl-1'}`}>
                  <button
                    onClick={() => {
                      navigate('/settings')
                      setProfileMenuOpen(false)
                      setIsOpen(false)
                    }}
                    className={`
                      w-full flex items-center gap-2.5 py-2 rounded-md text-sm transition-colors
                      ${isCollapsed ? 'justify-center px-0' : 'px-2'}
                      text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-black dark:hover:text-white
                    `}
                    tabIndex={profileMenuOpen ? 0 : -1}
                    title={isCollapsed ? 'Settings' : undefined}
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35A1.724 1.724 0 005.682 8.93c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {!isCollapsed && <span>Settings</span>}
                  </button>
                  <button
                    onClick={() => {
                      setProfileMenuOpen(false)
                      handleLogout()
                    }}
                    className={`
                      w-full flex items-center gap-2.5 py-2 rounded-md text-sm transition-colors
                      ${isCollapsed ? 'justify-center px-0' : 'px-2'}
                      text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10
                    `}
                    tabIndex={profileMenuOpen ? 0 : -1}
                    title={isCollapsed ? 'Log out' : undefined}
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                    </svg>
                    {!isCollapsed && <span>Log out</span>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-[35] md:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden
        />
      )}
    </>
  )
}
